import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  Box, Typography, InputAdornment, TextField,
  IconButton, CircularProgress, Chip
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { components } from 'cng-web-lib'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import pathMap from 'src/paths/pathMap'
import { useHistory } from 'react-router-dom'

const { button: { CngButton } } = components

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 12,
      maxWidth: 720,
      width: '100%'
    }
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  titleText: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 600,
    fontSize: 15
  },
  infoBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    background: '#f0f7ff',
    border: '1px solid #b3d4f5',
    borderRadius: 8,
    marginBottom: 12
  },
  searchBar: {
    marginBottom: 12
  },
  vesselList: {
    maxHeight: 420,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 0
  },
  vesselRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    '&:last-child': {
      borderBottom: 'none'
    },
    '&:hover': {
      background: theme.palette.action.hover
    }
  },
  vesselName: {
    fontWeight: 600,
    fontSize: 13,
    marginBottom: 3
  },
  vesselMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap'
  },
  chip: {
    fontSize: 11,
    height: 20,
    background: '#e8edf2',
    color: '#374151'
  },
  metaText: {
    fontSize: 11,
    color: theme.palette.text.secondary
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: theme.palette.text.secondary
  },
  footer: {
    padding: '12px 20px',
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  footerNote: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  footerActions: {
    display: 'flex',
    gap: 8
  }
}))

const POWER_TYPE_LABELS = { '1': 'Diesel', '2': 'Diesel and electric', '9': 'LNG' }

/**
 * VesselMasterPickerDialog
 *
 * Props:
 *   open        {boolean}  - dialog open state
 *   onClose     {fn}       - called when dialog is dismissed
 *   onSelect    {fn}       - called with the selected vessel object
 */
function VesselMasterPickerDialog({ open, onClose, onSelect }) {
  const classes = useStyles()
  const history = useHistory()

  const [vessels, setVessels] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [warnOpen, setWarnOpen] = useState(false)

  function handleManageClick() {
    setWarnOpen(true)
  }

  function handleWarnConfirm() {
    setWarnOpen(false)
    onClose && onClose()
    history.push(pathMap.UAE_MPCI_VESSEL_MASTER_LIST_VIEW)
  }

  const fetchVessels = useCallback(() => {
    setLoading(true)
    fetch(UaeMpciApiUrls.VESSEL_MASTER_LIST, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      },
      body: JSON.stringify({ page: 0, size: 200, sortBy: 'vesselName', sortDir: 'ASC' })
    })
      .then((res) => res.json())
            .then((json) => {
        const data =
          json?.data?.records?.content ??
          json?.data?.content ??
          json?.data ??
          []
        setVessels(Array.isArray(data) ? data : [])
        setLoading(false)
      })
      .catch(() => {
        setVessels([])
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    if (open) {
      setSearchText('')
      fetchVessels()
    }
  }, [open, fetchVessels])

  const filtered = vessels.filter((v) => {
    const q = searchText.toLowerCase()
    if (!q) return true
    return (
      (v.vesselName || '').toLowerCase().includes(q) ||
      (v.imoNumber || '').toLowerCase().includes(q) ||
      (v.carrierIdentifier || '').toLowerCase().includes(q) ||
      (v.vesselFlagCountry || '').toLowerCase().includes(q) ||
      (v.vesselPowerTypeDescription || '').toLowerCase().includes(q)
    )
  })

  function handleSelect(vessel) {
    onSelect && onSelect(vessel)
    onClose && onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog} maxWidth='md'>
      {/* Title */}
      <Box className={classes.dialogTitle}>
        <Typography className={classes.titleText}>
          <FontAwesomeIcon icon={['fal', 'ship']} style={{ fontSize: 15, opacity: 0.7 }} />
          Pick from Vessel Master
        </Typography>
        <IconButton size='small' onClick={onClose}>
          <FontAwesomeIcon icon={['fal', 'xmark']} style={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box style={{ padding: '16px 20px', overflowY: 'auto' }}>
        {/* Info banner */}
        <Box className={classes.infoBanner}>
          <FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 13, flexShrink: 0, color: '#2563eb' }} />
          <Typography variant='caption' style={{ color: '#1e40af' }}>
            Select a vessel from your master list to auto-fill the Vessel &amp; Voyage section. The voyage number is filing-specific and stays editable.
          </Typography>
        </Box>

        {/* Search */}
        <TextField
          fullWidth
          size='small'
          variant='outlined'
          placeholder='Search by Vessel Name, IMO, SMDG, Flag, Power Type...'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className={classes.searchBar}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <FontAwesomeIcon icon={['fal', 'magnifying-glass']} style={{ fontSize: 13, opacity: 0.5 }} />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position='end'>
                <IconButton size='small' onClick={() => setSearchText('')}>
                  <FontAwesomeIcon icon={['fal', 'xmark']} style={{ fontSize: 12 }} />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        {/* Vessel list */}
        <Box border={1} borderColor='divider' borderRadius={8} style={{ overflow: 'hidden' }}>
          {loading ? (
            <Box display='flex' alignItems='center' justifyContent='center' style={{ padding: 40 }}>
              <CircularProgress size={24} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box className={classes.emptyState}>
              <FontAwesomeIcon icon={['fal', 'ship']} style={{ fontSize: 28, marginBottom: 8, opacity: 0.3 }} />
              <Typography variant='body2'>No vessels found</Typography>
              <Typography variant='caption'>Try adjusting your search or add a new vessel to your master list.</Typography>
            </Box>
          ) : (
            <Box className={classes.vesselList}>
              {filtered.map((vessel) => (
                <Box key={vessel.id} className={classes.vesselRow} onClick={() => handleSelect(vessel)}>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Box display='flex' alignItems='center' style={{ gap: 6 }}>
                      <FontAwesomeIcon icon={['fal', 'ship']} style={{ fontSize: 12, opacity: 0.5 }} />
                      <Typography className={classes.vesselName}>{vessel.vesselName}</Typography>
                    </Box>
                    <Box className={classes.vesselMeta}>
                      {vessel.imoNumber && (
                        <Chip label={`IMO ${vessel.imoNumber}`} size='small' className={classes.chip} />
                      )}
                      {vessel.carrierIdentifier && (
                        <Chip label={`SMDG ${vessel.carrierIdentifier}`} size='small' className={classes.chip} />
                      )}
                      {vessel.vesselFlagCountry && (
                        <span className={classes.metaText}>{vessel.vesselFlagCountry}</span>
                      )}
                      {vessel.vesselFlagCountry && <span className={classes.metaText}>-</span>}
                      {vessel.vesselFlagCountry && (
                        <span className={classes.metaText}>{getCountryLabel(vessel.vesselFlagCountry)}</span>
                      )}
                      {vessel.vesselOwnershipIndicatorCode && (
                        <span className={classes.metaText}>{vessel.vesselOwnershipIndicatorCode}</span>
                      )}
                      {vessel.vesselPowerTypeCode && (
                        <span className={classes.metaText}>
                          {vessel.vesselPowerTypeCode} - {POWER_TYPE_LABELS[vessel.vesselPowerTypeCode] || vessel.vesselPowerTypeDescription || ''}
                        </span>
                      )}
                      {vessel.vesselPowerTypeDescription && !POWER_TYPE_LABELS[vessel.vesselPowerTypeCode] && (
                        <span className={classes.metaText}>{` \u00B7 ${vessel.vesselPowerTypeDescription}`}</span>
                      )}
                    </Box>
                  </Box>
                  <FontAwesomeIcon icon={['fal', 'chevron-right']} style={{ fontSize: 12, opacity: 0.4, flexShrink: 0 }} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Footer */}
      <Box className={classes.footer}>
        <Typography className={classes.footerNote}>
          <FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11 }} />
          Showing vessels from your{' '}
          <span
            style={{ color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={handleManageClick}
          >
            Vessel Master
          </span>
        </Typography>
        <Box className={classes.footerActions}>
          <CngButton color='secondary' size='small' onClick={onClose}
            startIcon={<FontAwesomeIcon icon={['fal', 'xmark']} />}>
            Cancel
          </CngButton>
          <CngButton color='primary' size='small'
            onClick={handleManageClick}
            startIcon={<FontAwesomeIcon icon={['fal', 'arrow-up-right-from-square']} />}>
            Manage Vessel Master
          </CngButton>
        </Box>
      </Box>
      {/* Unsaved draft warning */}
      <Dialog open={warnOpen} onClose={() => setWarnOpen(false)} maxWidth='xs' fullWidth>
        <Box style={{ padding: '20px 24px' }}>
          <Box display='flex' alignItems='center' style={{ gap: 10, marginBottom: 12 }}>
            <FontAwesomeIcon icon={['fal', 'triangle-exclamation']} style={{ fontSize: 20, color: '#d97706' }} />
            <Typography style={{ fontWeight: 600, fontSize: 15 }}>Unsaved changes may be lost</Typography>
          </Box>
          <Typography variant='body2' style={{ color: '#4b5563', marginBottom: 20 }}>
            You are about to leave this page. Any unsaved draft changes will be lost. Please save your draft before proceeding.
          </Typography>
          <Box display='flex' justifyContent='flex-end' style={{ gap: 8 }}>
            <CngButton color='secondary' size='small' onClick={() => setWarnOpen(false)}
              startIcon={<FontAwesomeIcon icon={['fal', 'arrow-left']} />}>
              Stay on Page
            </CngButton>
            <CngButton color='primary' size='small' onClick={handleWarnConfirm}
              startIcon={<FontAwesomeIcon icon={['fal', 'arrow-up-right-from-square']} />}>
              Leave Anyway
            </CngButton>
          </Box>
        </Box>
      </Dialog>
    </Dialog>
  )
}

function getCountryLabel(code) {
  // Simple label — just show the code; full country name lookup can be added later
  return code || ''
}

export default VesselMasterPickerDialog
